import { Card, CardContent, Checkbox, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Select, Snackbar, Typography, Autocomplete, TextField } from '@mui/material';
import { Alert } from '@mui/material';
import axios from 'axios';
import React, { useEffect, useMemo } from 'react';
import { LocalizeContextProps, Translate, withLocalize } from 'react-localize-redux';
import Form from '../../../components/general/form';
import { ButtonAccept, ButtonCancel, ButtonOk } from '../../../components/general/mini_components';
import Loader from '../../../components/Loader';
import { IDepartment, IUnit, SnackbarTypeSeverity } from '../../../constants/types';
import { useSnackBarState, SnackbarType, useUnitSelector, useDeparmentsSelector, useProfileInfo, useCurrentInvestigation } from '../../../hooks';
import { getCurrentResearcherUuid, requestTypeToTranslation } from '../../../utils/index.jsx';
import { TabsSherwood } from '../../components/Tabs';
import { IRequest, IServiceInvestigation, RequestType, ServiceType } from './types';
import ICTSelectorGeneral from '../../../components/general/SmartFields/ICT';
import { Diagnosis, SmartFieldType } from '../../../components/general/SmartFields';
import { QuillWrapper } from '../../../components/general/FieldSherwood';
import ReactQuill from 'react-quill';
import { CheckCircle } from '@mui/icons-material';
import DuplicateRequestModal, { DuplicateRequestService } from './DuplicateRequestModal';
import { getDuplicateRequestWarningsService } from '../../../services/request';
import RequestedSamplingField from './RequestedSamplingField';
import { DEFAULT_REQUESTED_SAMPLING, RequestedSampling } from './requestedSampling';
import { FUNCTIONALITY } from '../../../constants/types';

interface RequestFormProps {
    serviceType: ServiceType;
    uuidPatient:string,
    uuidInvestigation:string,
    units:IUnit[],
    initServicesInvestigation?: IServiceInvestigation[],
    cancel?:() => void,
    callBackRequestFinished:(requestsService:IRequest)=>void,  
}

export interface SurgeryRequestData {
    ictCode?: string; // ICT code for surgery reason
    surgicalIndication?: string; // Text description of the reason for the surgery
}


const RequestForm: React.FC<RequestFormProps> = ({ uuidPatient, units, serviceType, uuidInvestigation, initServicesInvestigation, callBackRequestFinished, cancel }) => {
    const [loading, setLoading] = React.useState(false);
    const [snackbar, setShowSnackbar] = useSnackBarState();
    const [servicesInvestigation, setServicesInvestigation] = React.useState<null | IServiceInvestigation[]>(initServicesInvestigation ? initServicesInvestigation : null);
    const [request, setRequest] = React.useState<null | IRequest>(null);
    // SHER-575: the order the doctor submitted, parked while they confirm that the
    // repeated laboratory tests listed in the modal are deliberate.
    const [pendingRequest, setPendingRequest] = React.useState<null | {servicesInvestigation:number[], uuidDepartment:string | null, ictCode:SurgeryRequestData | null}>(null);
    const [duplicates, setDuplicates] = React.useState<DuplicateRequestService[]>([]);
    const {profile, loadingProfile} = useProfileInfo();

    function handleClose(){
        setShowSnackbar({show:false});
        if(snackbar.severity === SnackbarTypeSeverity.SUCCESS && request !== null){
            callBackRequestFinished(request);
        }
    }

    
    /**
     * SHER-575 — laboratory orders get a duplicate confirmation; nothing else does.
     *
     * The narrowing to LABORATORY is the whole point and not an optimisation: this
     * component is the shared request form for Laboratory, Radiology, Cordonnerie AND
     * Surgery, so anything added here is seen by all four unless it is fenced off. The
     * API still refuses a repeat for the other three, and a confirmation dialog in front
     * of a refusal is just a second dead end.
     */
    function submitRequest(uuidPatient:string, servicesInvestigationIds:number[], uuidDepartment:string | null, ictCode: SurgeryRequestData| null = null) {
        if(serviceType !== ServiceType.LABORATORY){
            makeRequest(uuidPatient, servicesInvestigationIds, uuidDepartment, ictCode);
            return;
        }
        setLoading(true);
        getDuplicateRequestWarningsService(uuidInvestigation, uuidPatient, servicesInvestigationIds)
        .then(response => {
            const warnings = response.warnings ? response.warnings : [];
            if(warnings.length === 0){
                makeRequest(uuidPatient, servicesInvestigationIds, uuidDepartment, ictCode);
                return;
            }
            setLoading(false);
            setDuplicates(warnings.map((warning) => {
                const selected = (servicesInvestigation ? servicesInvestigation : []).find((service) => service.id === warning.serviceInvestigationId);
                return {
                    serviceInvestigationId: warning.serviceInvestigationId,
                    name: selected ? selected.description : String(warning.serviceInvestigationId),
                    lastRequestedAt: warning.lastRequestedAt,
                };
            }));
            setPendingRequest({servicesInvestigation:servicesInvestigationIds, uuidDepartment, ictCode});
        })
        .catch(() => {
            // The check is advisory, so a failure must not cost the doctor their order:
            // losing it would be a worse regression than the block this ticket removes.
            // Worst case they lose the warning, which is exactly the world before
            // SHER-575 minus the refusal.
            makeRequest(uuidPatient, servicesInvestigationIds, uuidDepartment, ictCode);
        });
    }

    function confirmDuplicateRequest(){
        if(!pendingRequest){
            return;
        }
        const confirmed = pendingRequest;
        setPendingRequest(null);
        setDuplicates([]);
        makeRequest(uuidPatient, confirmed.servicesInvestigation, confirmed.uuidDepartment, confirmed.ictCode);
    }

    function makeRequest(uuidPatient:string, servicesInvestigation:number[], uuidDepartment:string | null, ictCode: SurgeryRequestData| null = null) {
        setLoading(true);
        const postObject = {
            servicesInvestigationId: servicesInvestigation,
            uuidPatientInvestigation:uuidPatient,
            typeRequest: serviceType === ServiceType.SURGERY ? RequestType.SURGERY : RequestType.SERVICES,
            uuidDepartment:uuidDepartment,
            // SHER-910: this form's department selector is the requesting service ("Service
            // demandeur"). Said explicitly because uuidDepartment alone also carries a destination
            // on the agenda path; without it the API falls back to the doctor's roster.
            uuidRequesterDepartment:uuidDepartment,
            typeSpecificData: ictCode,
        }
        axios.post(import.meta.env.VITE_APP_API_URL+"/hospital/"+uuidInvestigation+"/service/request", postObject, { headers: {"Authorization" : localStorage.getItem("jwt") }})
        .then(response => {
            if(response.status === 200){
                let message = "pages.hospital.services.success";
                if(serviceType === ServiceType.SURGERY){
                    message = "pages.hospital.services.success_surgery";
                }
                setShowSnackbar({show:true, severity:"success", message:message});
                setRequest(response.data.request);
            }
            else{
                setShowSnackbar({show:true, severity:"error", message:"general.error"});
            }
            setLoading(false);
        })
        .catch(error => {
            console.log(error.response.data.errorCode);
            let translateError = "general.error";
            if(error.response.data.errorCode === "PENDING_REQUEST_FOR_PATIENT"){
                translateError = "pages.hospital.services.error.previous_request";
            }
            setLoading(false);
            setShowSnackbar({show:true, severity:"error", message:translateError});
          
        })
    }
    useEffect(() => {
        if(!servicesInvestigation){
            setLoading(true);
            axios(import.meta.env.VITE_APP_API_URL+"/hospital/"+uuidInvestigation+"/services/"+serviceType, { headers: {"Authorization" : localStorage.getItem("jwt") }})
            .then(response => {
                setServicesInvestigation(response.data.servicesInvestigation);
                setLoading(false);
            })
        }

    }, []);

    if(loadingProfile){
        return <Loader />;
    }
    return <>
                <DuplicateRequestModal open={pendingRequest !== null} loading={loading} duplicates={duplicates}
                    closeModal={() => { setPendingRequest(null); setDuplicates([]); }}
                    onConfirm={confirmDuplicateRequest} />
                <RequestFormCoreLocalized loading={loading} snackbar={snackbar} servicesInvestigation={servicesInvestigation ? servicesInvestigation : []}
                    handleCloseSnackBar={handleClose} cancel={cancel} units={units} serviceType={serviceType}
                    uuidResearcher={profile.uuid}
                    callBackFormSubmitted={(servicesInvestigation:number[], uuidDepartment:string | null, ictCode: SurgeryRequestData| null = null) => 
                        submitRequest(uuidPatient, servicesInvestigation, uuidDepartment, ictCode)} />
            </>;
}

export default RequestForm;

interface RequestFormCoreProps extends Omit<RequestFormProps, 'uuidPatient' | 'uuidInvestigation' | 'callBackRequestFinished' > , LocalizeContextProps {
    loading: boolean;
    snackbar: SnackbarType;
    uuidResearcher:string,
    servicesInvestigation:IServiceInvestigation[],
    handleCloseSnackBar:()=>void,
    callBackFormSubmitted:(serviceInvestigation:number[], uuidDepartment:string | null, ictCode?: SurgeryRequestData| null) => void
}

// Add TypeScript declaration for import.meta.env
declare global {
    interface ImportMeta {
        env: {
            VITE_APP_API_URL: string;
            [key: string]: string;
        };
    }
}

export const RequestFormCore: React.FC<RequestFormCoreProps> = ({ loading, servicesInvestigation, snackbar, uuidResearcher, serviceType, units, activeLanguage,
                                                                    translate, callBackFormSubmitted, handleCloseSnackBar, cancel }) => {
    const [servicesInvestigationSelected, setServicesInvestigationSelected] = React.useState<{ [id: string] : boolean; }>({});
    const [errorServices, setErrorServices] = React.useState(false);
    const [formSubmitted, setFormSubmitted] = React.useState(false);
    const [selectedIctCode, setSelectedIctCode] = React.useState<string | null>(null);
    const [surgeryIndication, setSurgeryIndication] = React.useState<string | null>(null);
    const [ictCodeError, setIctCodeError] = React.useState(false);
    const [notes, setNotes] = React.useState<string | null>(null);
    const [requestedSampling, setRequestedSampling] = React.useState<RequestedSampling>(DEFAULT_REQUESTED_SAMPLING);
    const { hasFunctionality } = useCurrentInvestigation();
    const showRequestedSampling = serviceType === ServiceType.LABORATORY
        && hasFunctionality(FUNCTIONALITY.REPEATED_LAB_REQUESTS);

    // SHER-446: pre-fill the requesting department from the doctor's own service.
    // Pass the current researcher + onlyResearcherIfSingle so that a doctor in a
    // single department gets it auto-selected and hidden; doctors in zero or
    // several departments keep the full hospital list (unchanged behaviour).
    const {renderDepartmentSelector, departmentSelected, markAsErrorDepartmentCallback} = useDeparmentsSelector(false, getCurrentResearcherUuid(), true, undefined, true);

    useEffect(() => {
        if(!loading){
            setFormSubmitted(false);
        }
    }, [loading]);

    const getServiceByCategory = (category:string | undefined) => {
        const filterServicesInvestigation = category ? servicesInvestigation.filter((serviceInv) => serviceInv.service.category === category) : servicesInvestigation;
        return filterServicesInvestigation.map((serviceInv) => {
            return {
                ...serviceInv,
                service: {
                    ...serviceInv.service,
                }
               }           })
    }
    const serviceCategories = useMemo(() => {
        let categories:{[id:string]:IServiceInvestigation[]} = {};
        servicesInvestigation.forEach(serviceInvestigation => {
            if(!categories[serviceInvestigation.service.category]){
                categories[serviceInvestigation.service.category] = [];
            }
            categories[serviceInvestigation.service.category].push(serviceInvestigation);
        });
        return categories;
    }, [servicesInvestigation]);
    const typeTestString = requestTypeToTranslation(serviceType);
    const SERVICE_SEPARATOR = "service_";
    
    function renderIctSelector() {
        return (
            <Grid
                item
                xs={12}
                sx={{
                    width: '100%',
                    // ICTSelectorGeneral wraps its input in a <Grid container spacing={3}>,
                    // whose negative margin indents the field and makes it render narrower
                    // than the sibling department/service selectors. Neutralise that inset so
                    // the three surgical-request fields share the same full width.
                    '& .MuiGrid-container': { margin: 0, width: '100%' },
                    '& .MuiGrid-container > .MuiGrid-item': { paddingTop: 0, paddingLeft: 0 },
                }}
            >
                <ICTSelectorGeneral
                    variant="outlined"
                    size="lg"
                    language={activeLanguage.code}
                    typeMargin="normal"
                    type="ICT"
                    label={translate("pages.hospital.services.request.surgery_indication").toString()}
                    elementSelected={(element: SmartFieldType) => {
                        if ('ict' in element && 'ict-code' in element) {
                            setSelectedIctCode(element['ict-code'] as string);
                            setSurgeryIndication(element['ict'] as string);
                            if (element['ict-code']) setIctCodeError(false);
                        }
                    }}
                    error={ictCodeError}
                />
            </Grid>
        );
    }
  
    function callBackForm(){
        const serviceInvestigationIds = Object.keys(servicesInvestigationSelected).filter((key) => servicesInvestigationSelected[key] === true).map((key) => parseInt(key.replace(SERVICE_SEPARATOR, "")));
        
        // Check if we need to validate ICT code (only for surgery)
        const needIctCode = serviceType === ServiceType.SURGERY;
        const ictCodeValid = !needIctCode || (needIctCode && selectedIctCode !== null);
        
        if((departmentSelected === null && units.length > 0) || serviceInvestigationIds.length === 0 || !ictCodeValid){
            if(departmentSelected === null){
                markAsErrorDepartmentCallback();
            }
            if(serviceInvestigationIds.length === 0){
                setErrorServices(true);  
                setFormSubmitted(false); 
            }
            if(needIctCode && !ictCodeValid){
                setIctCodeError(true);
            }
            return;
        }
    
        console.log("callBackForm", servicesInvestigationSelected);
        setFormSubmitted(true);
        console.log("serviceInvestigationIds", serviceInvestigationIds);
        let extraParams = null;
        if(serviceType === ServiceType.SURGERY){
            extraParams = {
                ictCode: selectedIctCode,
                ict: surgeryIndication,
            }
        }
        else{
            // SHER-1043: laboratory requests under REPEATED_LAB_REQUESTS carry the
            // sampling choice next to the existing notes payload.
            extraParams = {
                notes: notes,
                ...(showRequestedSampling ? { requestedSampling } : {}),
            }
        }
        callBackFormSubmitted(serviceInvestigationIds, departmentSelected, extraParams);
    }

    function renderServiceCheckboxes(category?:string){
        const services = getServiceByCategory(category);
        return services.sort((serviceA, serviceB) => {
            const labelA = serviceA.description.toString();
            const labelB = serviceB.description.toString();
            return labelA.localeCompare(labelB, activeLanguage.code);
        }).map((serviceInvestigation) => {
                return <Grid item xs={6}>
                        <FormControlLabel
                            control={<Checkbox checked={servicesInvestigationSelected[SERVICE_SEPARATOR+serviceInvestigation.id]} />}
                            onChange={
                                (event: React.ChangeEvent<{}>, checked: boolean) => {
                                    setServicesInvestigationSelected({...servicesInvestigationSelected, ["service_"+serviceInvestigation.id]:checked});
                                }
                            }
                            label={serviceInvestigation.description} 
                        />
                        </Grid>
        })
    }

    function renderServiceAutocomplete(category?: string) {
        let services = getServiceByCategory(category);
        const sortedServiceInvestigation = services.sort((serviceA, serviceB) => {
            const labelA = serviceA.description.toString();
            const labelB = serviceB.description.toString();
            return labelA.localeCompare(labelB, activeLanguage.code);
        });
        return (
            <Grid item xs={12}>
                <Autocomplete
                    multiple
                    options={sortedServiceInvestigation}
                    getOptionLabel={(option) => option.description}
                    onChange={(event, newValue) => {
                        const newSelected = { ...servicesInvestigationSelected };
                        // Reset all selections
                        services.forEach(service => {
                            newSelected[SERVICE_SEPARATOR + service.id] = false;
                        });
                        // Set new selections
                        newValue.forEach(service => {
                            newSelected[SERVICE_SEPARATOR + service.id] = true;
                        });
                        setServicesInvestigationSelected(newSelected);
                    }}
                    value={services.filter(service => servicesInvestigationSelected[SERVICE_SEPARATOR + service.id])}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            error={errorServices}
                            variant="outlined"
                            label={<Translate id="pages.hospital.services.request.select_services_surgery" />}
                        />
                    )}
                />
            </Grid>
        );
    }
    
    function renderTabsServices(){
        if(Object.values(serviceCategories).length === 1){
            return(
                <Grid container item xs={12} spacing={1}>
                    { renderServiceCheckboxes() }
                </Grid>
            )
        }
        else{
            const orderedLabels = Object.keys(serviceCategories).map((category)=> {return {
                label : translate(`pages.hospital.${typeTestString}.category.${category.toLocaleLowerCase()}`).toString() ,
                key: category
            }}).sort((labelA, labelB) => labelA.label.localeCompare(labelB.label, activeLanguage.code))
            return (
                <TabsSherwood name="Requests" whiteBackground={true}
                    labels={orderedLabels.map((orderedLabel) => orderedLabel.label) } >
                        { orderedLabels.map((orderedLabel) => orderedLabel.key).map((serviceCategory) => {
                            return <Grid container item xs={12} spacing={1}>
                                { renderServiceCheckboxes(serviceCategory) }
                            </Grid>
                        }) }
                        <></>
                </TabsSherwood>
            ); 
        }
    }

    function renderNotes(){
        return(
            <Grid item xs={12} paddingTop={3}>
                 <Typography variant="body2" gutterBottom>
                    <Translate id="pages.hospital.services.request.notes" />:
                </Typography>
                <QuillWrapper >
                    <ReactQuill
                        style={{ fontSize: "24px" }}
                        value={notes || ""}
                        onChange={(newValue, delta, source) => {
                        if (source === "user") {
                            setNotes(newValue);
                        }
                        }}
                        onBlur={(range, source, quill) => {
                            setNotes(quill.getHTML());
                        }}
                    />
                </QuillWrapper>
            </Grid>
        )
    }
    function renderCore(){
        if(serviceType === ServiceType.SURGERY){
            return(
                <div className="ict-surgery-container" style={{ width: '100%' }}>
                    {renderDepartmentSelector()}
                    {renderIctSelector()}
                    {renderServiceAutocomplete()}
                </div>
            ) 
        }
        else{
            return(
                <>
                    {renderDepartmentSelector()}
                    {renderTabsServices()}
                    {showRequestedSampling && (
                        <Grid item xs={12}>
                            <RequestedSamplingField
                                value={requestedSampling}
                                onChange={setRequestedSampling}
                            />
                        </Grid>
                    )}
                    {renderNotes()}
                </>
            ) 
        }
    }

    if(loading){
        return <Loader />;
    }

    return (
        <>  
            <Snackbar
                anchorOrigin={{
                vertical: 'top',
                horizontal: 'center',
                }}
                open={snackbar.show}
                autoHideDuration={4000}
                onClose={handleCloseSnackBar}
                >
                    <Alert severity={snackbar.severity}>
                            <Translate id={snackbar.message} />                            
                        </Alert>
                </Snackbar>
                <Grid container spacing={3}>
                    <Grid xs={12} item>
                        <Card>
                            <CardContent>
                                <Typography variant="subtitle1" ><Translate id={`hospital.request.${requestTypeToTranslation(serviceType)}`} /></Typography>
                                {
                                    renderCore()
                                }
                            </CardContent>
                        </Card>
                </Grid>
                {
                    errorServices && serviceType !== ServiceType.SURGERY &&
                    <Grid item xs={12}>
                        <Alert severity="error">
                            <Translate id="pages.hospital.services.request.select_services" />
                        </Alert>
                    </Grid>
                }
                <Grid container item xs={12}>
                    <Grid item>
                        <ButtonOk disabled={formSubmitted || loading || snackbar.show} onClick={callBackForm}><Translate id="pages.hospital.services.request.make_request" /></ButtonOk> 
                    </Grid>
                    <Grid item style={{marginLeft:"1rem"}}>
                        <ButtonCancel disabled={loading || snackbar.show} onClick={cancel} ><Translate id="general.cancel" /></ButtonCancel>
                    </Grid>
                </Grid>   
            </Grid>
        </>
    );
};

export const RequestFormCoreLocalized = withLocalize(RequestFormCore)
